import React from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import {
  brandColours,
  breakpointSizes,
  fluidFontSize,
  fontSize,
  fontWeights,
  maxBreakpointQuery,
  minBreakpointQuery,
  sectionPaddings,
  standardColours,
} from '../styles';
import { Button, Container, HtmlContent, Link, Tag } from './ui';
import { hasStructuredText } from '../utils';
import BlogMeta from './BlogMeta';

const StyledBlogDetailBanner = styled.section`
  ${sectionPaddings('45px', '90px')};
  background-color: ${brandColours.primary};
`;

const StyledInner = styled.article`
  display: grid;
  gap: 30px;

  ${minBreakpointQuery.tiny`
    gap: 40px;
  `}

  ${minBreakpointQuery.small`
    gap: 50px;
  `}

  ${minBreakpointQuery.smedium`
    grid-template-columns: repeat(2, 1fr);
    align-items: center;
  `}

  ${minBreakpointQuery.medium`
    gap: 60px;
  `}

  ${minBreakpointQuery.large`
    gap: 70px;
  `}

  ${minBreakpointQuery.xxlarge`
    gap: 80px;
  `}
`;

const StyledContent = styled.div``;

const StyledTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 20px;
`;

const StyledHeading = styled.h1`
  color: ${brandColours.secondary};
  font-weight: ${fontWeights.semibold};
  ${fluidFontSize(
    '24px',
    '40px',
    breakpointSizes.tiny,
    breakpointSizes.xxxxlarge,
  )};
  line-height: 1.2;
`;

const StyledSummary = styled(HtmlContent)`
  margin-top: 30px;
  p {
    line-height: 1.7;
  }
`;

const StyledText = styled.p`
  margin-top: 30px;
  line-height: 1.7;
`;

const StyledFeaturedResource = styled.div`
  background-color: ${brandColours.octonary};
  border-radius: 12px;
  position: relative;
  padding: 20px;

  ${minBreakpointQuery.small`
    padding: 10px 30px 30px 30px; 
  `};

  ${minBreakpointQuery.smedium`
    padding-bottom: 60px;
  `};

  ${minBreakpointQuery.medium`
    padding-bottom: 50px;
    padding-left: 40px;
    padding-right: 40px;
  `};

  ${minBreakpointQuery.xlarge`
    padding-bottom: 40px;
  `};
`;

const StyledImage = styled(GatsbyImage)`
  width: 100%;
`;

const StyledFeaturedResourceTag = styled.span`
  ${fontSize(11)};
  background-color: ${brandColours.denary};
  color: ${standardColours.white};
  border-radius: 50%;
  position: absolute;
  height: 75px;
  width: 75px;
  text-align: center;
  padding: 20px 4px;
  border: solid 2px ${standardColours.white};
  top: -25px;
  left: -25px;
  text-transform: uppercase;

  ${minBreakpointQuery.small`
    height: 85px;
    width: 85px;
    top: -30px;
    left: -30px;
    padding: 25px 5px;
    ${fontSize(12)};
  `}

  ${minBreakpointQuery.large`
    height: 100px;
    width: 100px;
    top: -35px;
    left: -35px;
    padding: 30px 6px;
    ${fontSize(13)};
  `}
`;

const StyledButton = styled(Button)`
  ${fontSize(14)};
  padding: 16px 32px;

  ${maxBreakpointQuery.small`
    display: block;
    margin: auto;
    margin-top: 15px;
  `};

  ${minBreakpointQuery.small`
    position: absolute;
    bottom: 25px;
    right: 25px;
  `};

  ${minBreakpointQuery.smedium`
    bottom: 15px;
    right: 15px;
  `};

  ${minBreakpointQuery.medium`
    bottom: 20px;
    right: 20px;
  `};

  ${minBreakpointQuery.mlarge`
    bottom: 25px;
    right: 25px;
  `}
`;

const BlogDetailBanner = ({ blogPost, resource }) => {
  const { categories, heading, summaryNew, summary, author, readTime } =
    blogPost;

  return (
    <StyledBlogDetailBanner>
      <Container>
        <StyledInner>
          <StyledContent>
            <StyledTags>
              {categories.map((category, id) => (
                <Link key={id} to={category}>
                  <Tag key={id}>{category.title}</Tag>
                </Link>
              ))}
            </StyledTags>
            <StyledHeading>{heading}</StyledHeading>
            {hasStructuredText(summaryNew) ? (
              <StyledSummary content={summaryNew} />
            ) : (
              <StyledText>{summary}</StyledText>
            )}
            <BlogMeta author={author} readTime={readTime} />
          </StyledContent>
          <StyledFeaturedResource>
            <StyledImage
              image={resource.ctaImage.gatsbyImageData}
              objectFit="contain"
              alt={resource.ctaImage.alt}
            />
            <StyledFeaturedResourceTag>Free Resource</StyledFeaturedResourceTag>
            <StyledButton to={resource.link.pageUrl}>
              {resource.ctaButtonText || 'Download now'}
            </StyledButton>
          </StyledFeaturedResource>
        </StyledInner>
      </Container>
    </StyledBlogDetailBanner>
  );
};

export default BlogDetailBanner;
